import { AlertCircle, CalendarClock } from 'lucide-react';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui';
import { useGenerateSchedule, useLoan } from '@/hooks/use-loans';
import { useDrawdownSummary } from '@/hooks/use-tranches';
import { fmtDateOnly } from '@/lib/format';
import { getScheduleActionLabel, isLineOfCredit } from '@/lib/loan-type-labels';
import type { RepaymentScheduleItem, ScheduleItemStatus } from '@/types';

function fmt(value: string): string {
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(num);
}

const STATUS_STYLES: Record<ScheduleItemStatus, { bg: string; text: string }> = {
  PENDING: { bg: 'bg-gray-100', text: 'text-gray-600' },
  PAID: { bg: 'bg-green-50', text: 'text-green-700' },
  PARTIAL: { bg: 'bg-amber-50', text: 'text-amber-700' },
  OVERDUE: { bg: 'bg-red-50', text: 'text-red-700' },
  WAIVED: { bg: 'bg-gray-50', text: 'text-gray-400' },
};

interface Props {
  loanId: string;
}

export function ScheduleTab({ loanId }: Props) {
  const { data: loan, isLoading } = useLoan(loanId);
  const generate = useGenerateSchedule();
  const isLoc = isLineOfCredit(loan?.loanType);
  const { data: drawdownSummary } = useDrawdownSummary(loanId);

  const schedule = loan?.schedule ?? [];
  const drawnBalance = drawdownSummary?.totalDrawn ?? 0;
  const canGenerateLocSchedule = drawnBalance > 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Spinner size="lg" className="w-5 h-5 animate-spin mr-2" />
        Loading…
      </div>
    );
  }

  if (schedule.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-400">
        <CalendarClock className="w-10 h-10 mb-3" />
        <p className="text-sm mb-2">
          {isLoc
            ? 'No line of credit interest schedule generated yet'
            : 'No repayment schedule generated yet'}
        </p>
        {isLoc && (
          <p className="mb-4 max-w-lg text-center text-xs text-gray-500">
            Interest is generated from the drawn balance only. Draw funds in the Tranches tab before
            generating a schedule.
          </p>
        )}
        <Button
          variant="primary"
          onClick={() => generate.mutate(loanId)}
          disabled={generate.isPending || (isLoc && !canGenerateLocSchedule)}
          className="w-fit flex items-center gap-2 px-4 py-2 text-white rounded-md text-sm font-medium hover: disabled:opacity-50 transition-colors"
        >
          {generate.isPending && <Spinner size="lg" className="w-4 h-4 animate-spin" />}
          {getScheduleActionLabel(loan?.loanType)}
        </Button>
        {isLoc && !canGenerateLocSchedule && (
          <p className="mt-3 text-xs text-amber-600">
            No drawn balance yet. Current drawn amount: {fmt(String(drawnBalance))}
          </p>
        )}
        {generate.isError && (
          <p className="mt-3 text-sm text-red-500 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {(generate.error as Error)?.message ?? 'Failed to generate'}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {isLoc && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          This schedule is interest-only on the drawn balance, with principal due at maturity.
          Regenerate it after additional drawdowns.
        </div>
      )}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">#</th>
              <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Due Date</th>
              <th className="text-right text-xs font-medium text-gray-500 px-4 py-3">Principal</th>
              <th className="text-right text-xs font-medium text-gray-500 px-4 py-3">Interest</th>
              <th className="text-right text-xs font-medium text-gray-500 px-4 py-3">Fees</th>
              <th className="text-right text-xs font-medium text-gray-500 px-4 py-3">Total</th>
              <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {schedule.map((item: RepaymentScheduleItem) => {
              const style = STATUS_STYLES[item.status] ?? STATUS_STYLES.PENDING;
              return (
                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-2.5 text-sm text-gray-500">{item.periodNumber}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-900">{fmtDateOnly(item.dueDate)}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-900 text-right">
                    {fmt(item.principalDue)}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-500 text-right">
                    {fmt(item.interestDue)}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-500 text-right">
                    {fmt(String(item.feesDue ?? 0))}
                  </td>
                  <td className="px-4 py-2.5 text-sm font-medium text-gray-900 text-right">
                    {fmt(item.totalDue)}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${style.bg} ${style.text}`}
                    >
                      {item.status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
